// Shared persistence for tooth-scoped diagnoses.
//
// Diagnosis already has a canonical domain (PRD.317): `patient_diagnosis_entries`
// holds the encounter's diagnoses and `patient_diagnosis_teeth` associates each
// with FDI tooth numbers. The Part Conditions modal writes through exactly that
// domain — a diagnosis picked on the chart is the SAME record the Diagnosis
// section shows, not a chart-local copy.
//
// Extracted here so the chart modal and `EncounterDiagnosesTab` share one set of
// statements instead of each maintaining its own.
//
// NOTE ON CHART INTERACTION: these writes are immediate and land in the
// diagnosis tables. Surface conditions and flags are staged in the chart's
// `pendingStates` and only reach `odontogram_charts.tooth_states` on "Save
// Chart". The two never touch the same storage, so neither can clobber the
// other — that independence is structural, not a convention to remember.

import { diagnosisToothId } from '@helios/data-access';

/** Minimal shape of the PowerSync handle these helpers need. */
interface Db {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface DiagnosisWriteContext {
  orgId: string;
  patientId: string;
  encounterId: string;
}

/**
 * Associate an existing diagnosis with teeth.
 *
 * The row id is DETERMINISTIC (`diagnosisToothId`), so two devices adding the
 * same tooth to the same diagnosis converge on one row instead of racing to
 * create duplicates.
 */
export async function insertDiagnosisTeeth(
  db: Db,
  ctx: DiagnosisWriteContext,
  diagnosisId: string,
  teeth: readonly string[],
): Promise<void> {
  const now = new Date().toISOString();
  for (const tooth of teeth) {
    await db.execute(
      `INSERT INTO patient_diagnosis_teeth
         (id, org_id, clinic_id, diagnosis_entry_id, patient_id, encounter_id, tooth,
          created_at, updated_at)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        diagnosisToothId(diagnosisId, tooth),
        ctx.orgId,
        null,
        diagnosisId,
        ctx.patientId,
        ctx.encounterId,
        tooth,
        now,
        now,
      ],
    );
  }
}

/**
 * Create a diagnosis for this encounter and attach it to one tooth.
 *
 * Returns the new entry id. Used by the chart modal, where a diagnosis is
 * always picked in the context of the tooth whose conditions are open.
 */
export async function addDiagnosisForTooth(
  db: Db,
  ctx: DiagnosisWriteContext,
  label: string,
  tooth: string,
): Promise<string | null> {
  const trimmed = label.trim();
  if (!trimmed) return null;
  const id = crypto.randomUUID();
  const now = new Date().toISOString();
  await db.execute(
    `INSERT INTO patient_diagnosis_entries
      (id, org_id, clinic_id, patient_id, encounter_id, label, created_at, updated_at)
     VALUES (?, ?, ?, ?, ?, ?, ?, ?)`,
    [id, ctx.orgId, null, ctx.patientId, ctx.encounterId, trimmed, now, now],
  );
  await insertDiagnosisTeeth(db, ctx, id, [tooth]);
  return id;
}

/**
 * Detach a diagnosis from ONE tooth.
 *
 * When that was its last tooth the diagnosis entry itself is removed: a
 * diagnosis created from the chart exists to describe that tooth, so leaving a
 * context-less entry behind in the Diagnosis section would be a surprise. A
 * diagnosis still linked to other teeth is left alone — only the association
 * for this tooth goes.
 */
export async function removeDiagnosisFromTooth(
  db: Db,
  diagnosisId: string,
  tooth: string,
  remainingTeeth: readonly string[],
): Promise<void> {
  await db.execute(`DELETE FROM patient_diagnosis_teeth WHERE id = ?`, [
    diagnosisToothId(diagnosisId, tooth),
  ]);
  const others = remainingTeeth.filter((t) => t !== tooth);
  if (others.length === 0) {
    await db.execute(`DELETE FROM patient_diagnosis_entries WHERE id = ?`, [diagnosisId]);
  }
}
